import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from './components/Header';
import { HeroSection } from './sections/HeroSection';
import { ProgressiveTextSection } from './sections/ProgressiveTextSection';
import { AboutSection } from './sections/AboutSection';
import { FeaturedProjectSection } from './sections/FeaturedProjectSection';
import { ProjectsGrid } from './sections/ProjectsGrid';
import { ParallaxCaseStudies } from './sections/ParallaxCaseStudies';
import { ExpertiseSection } from './sections/ExpertiseSection';
import { MetricsSection } from './sections/MetricsSection';
import { TestimonialsSection } from './sections/TestimonialsSection';
import { CTASection } from './sections/CTASection';
import { Footer } from './components/Footer';
import { CustomCursor } from './components/CustomCursor';
import { ScrollProvider } from './context/ScrollContext';
import { FloatingButtons } from './components/FloatingButtons';
import { ChatDrawer } from './components/ChatDrawer';
import { AnimationProvider } from './utils/AnimationObserver';
import { usePortfolio } from './context/PortfolioContext';
import { SEO } from './components/SEO';
import { StructuredData } from './components/StructuredData';
import './styles/animations.css';
export function App() {
  const {
    setViewPreference,
    isChatDrawerOpen
  } = usePortfolio();
  // Set scroll restoration to manual on app mount
  useEffect(() => {
    // Use history API to ensure scroll restoration is manual
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  // Set view preference to 'traditional' when this page loads - ONLY ONCE
  useEffect(() => {
    setViewPreference('traditional');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount
  // Dummy handler for ChatDrawer prop (not used anymore since we navigate directly)
  const handleCaseStudyClick = (slug: string) => {
    // This is now handled inside ChatDrawer via navigate()
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  return <>
      <SEO title="Home - Mateo Cohen" description="Lead AI Product Designer specializing in enterprise UX, design systems, and AI innovation. 8+ years experience in financial services, cybersecurity, and conversational AI design." url="https://mateocohen.com" type="website" data-id="element-0" />
      <StructuredData type="portfolio" data-id="element-1" />
      <ScrollProvider data-id="element-2">
        <AnimationProvider data-id="element-3">
          <div className="w-full min-h-screen bg-black text-white" data-id="element-4">
            <CustomCursor data-id="element-5" />

            {/* Flex container: Main content + Drawer side-by-side */}
            <div className="flex min-h-screen" data-id="element-6">
              {/* Main Content Area - Takes remaining space */}
              <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-7">
                <Header data-id="element-8" />

                {/* Portfolio Content */}
                <motion.div initial={{
                opacity: 1
              }} className="flex-1" data-id="element-9">
                  <HeroSection data-id="element-10" />
                  <ParallaxCaseStudies data-id="element-11" />
                  <ExpertiseSection data-id="element-12" />
                  <TestimonialsSection data-id="element-13" />
                  <AboutSection data-id="element-14" />
                  <MetricsSection data-id="element-15" />
                  <CTASection data-id="element-16" />
                  <Footer data-id="element-17" />
                </motion.div>
              </div>

              {/* Chat Drawer - Fixed width column on desktop, hidden on mobile */}
              <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-18" />
            </div>

            {/* Floating button - only show when drawer is CLOSED */}
            {!isChatDrawerOpen && <FloatingButtons data-id="element-19" />}
          </div>
        </AnimationProvider>
      </ScrollProvider>
    </>;
}